/**
 * Weglot language switcher shim.
 *
 * Loaded only when settings.language_switcher_provider == 'weglot'. The markup
 * in snippets/language-switcher-weglot.liquid mirrors the native switcher: the
 * shared m-select-component widget owns the open/close UX, trigger text, and
 * active highlight. This shim only bridges that widget to Weglot:
 *
 *   1. On the native select's `change` event, call Weglot.switchTo(code) so
 *      picking an option switches language (preserving the current path). The
 *      placeholder value `source` (merchant left the subdirectory blank) is
 *      resolved to `Weglot.options.language_from`.
 *   2. On load and on `languageChanged`, sync each switcher's selected value,
 *      trigger text, and active option to the current Weglot language.
 */
(function () {
  "use strict";

  var SWITCHER_SELECTOR = '[data-language-switcher="weglot"]';

  function getSourceLang() {
    try {
      if (window.Weglot && Weglot.options && Weglot.options.language_from) {
        return String(Weglot.options.language_from).toLowerCase();
      }
    } catch (e) {}
    return "";
  }

  function getCurrentLang() {
    try {
      if (window.Weglot && typeof Weglot.getCurrentLang === "function") {
        return String(Weglot.getCurrentLang() || "").toLowerCase();
      }
    } catch (e) {}
    return "";
  }

  // Map an option value to a real Weglot language code. The blank/source entry
  // uses the value 'source', resolved here to Weglot's source language.
  function resolveLang(value) {
    var v = (value || "").toLowerCase();
    if (v === "" || v === "source") return getSourceLang();
    return v;
  }

  // Bridge the native select's change event to Weglot.switchTo. The shared
  // m-select-component dispatches `change` on the select when an option row is
  // clicked, so this is all we need to actually switch language.
  function bindChangeHandlers() {
    var switchers = document.querySelectorAll(SWITCHER_SELECTOR);
    for (var i = 0; i < switchers.length; i++) {
      var select = switchers[i].querySelector("[data-weglot-select]");
      if (!select || select.getAttribute("data-weglot-bound") === "1") continue;
      select.setAttribute("data-weglot-bound", "1");
      select.addEventListener("change", function (e) {
        var lang = resolveLang(e.target.value);
        if (!lang) return;
        // TEMP DIAGNOSTIC: logs a stack trace whenever a switch is about to run.
        // isTrusted:false means the change was dispatched by script (e.g. the
        // widget's synthetic event), true means a real user interaction. Remove
        // once the mobile auto-switch trigger is identified.
        try {
          console.trace("[weglot-switch] about to switchTo", {
            value: e.target.value,
            resolved: lang,
            current: getCurrentLang(),
            isTrusted: e.isTrusted,
          });
        } catch (logErr) {}
        try {
          if (window.Weglot && typeof Weglot.switchTo === "function") {
            Weglot.switchTo(lang);
          }
        } catch (err) {}
      });
    }
  }

  // Sync selected value, trigger text, and active option to the current lang.
  function syncActiveState() {
    var current = getCurrentLang();
    if (!current) return;

    var switchers = document.querySelectorAll(SWITCHER_SELECTOR);
    for (var i = 0; i < switchers.length; i++) {
      var switcher = switchers[i];
      var options = switcher.querySelectorAll(".m-select-custom--option");
      var select = switcher.querySelector("[data-weglot-select]");
      var activeLabel = "";
      var activeValue = "";

      for (var j = 0; j < options.length; j++) {
        var opt = options[j];
        var optLang = resolveLang(opt.getAttribute("data-value"));
        var isActive = optLang === current;
        opt.classList.toggle("isActive", isActive);
        if (isActive) {
          activeLabel = opt.textContent;
          activeValue = opt.getAttribute("data-value");
        }
      }

      if (activeValue && select) {
        select.value = activeValue;
      }

      // Icon mode renders only the globe in the trigger: labels live solely in
      // the options list, so keep the trigger text empty (mirrors the native
      // icon switcher). Non-icon mode shows the active label in the trigger.
      var isIconMode =
        switcher.getAttribute("data-weglot-display") === "icon";
      var triggerText = switcher.querySelector(
        ".m-select-custom--trigger-text",
      );
      if (triggerText) {
        if (isIconMode) {
          triggerText.textContent = "";
        } else if (activeLabel) {
          triggerText.textContent = activeLabel.trim();
        }
      }
    }
  }

  function onWeglotReady() {
    bindChangeHandlers();
    syncActiveState();

    try {
      if (window.Weglot && typeof Weglot.on === "function") {
        Weglot.on("languageChanged", function () {
          syncActiveState();
        });
      }
    } catch (e) {}
  }

  function init() {
    var ready = false;

    try {
      if (window.Weglot && typeof Weglot.on === "function") {
        Weglot.on("initialized", function () {
          ready = true;
          onWeglotReady();
        });
      }
    } catch (e) {}

    // Fallback: Weglot may already be initialized (or the 'initialized' event
    // may have fired before this script attached). Poll briefly until the API
    // is usable, then run once.
    var attempts = 0;
    var timer = setInterval(function () {
      attempts++;
      if (ready) {
        clearInterval(timer);
        return;
      }
      if (getSourceLang() || getCurrentLang()) {
        clearInterval(timer);
        onWeglotReady();
        return;
      }
      if (attempts > 50) {
        clearInterval(timer);
      }
    }, 200);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
